// Brute force protection utilities
const STORAGE_KEY_PREFIX = 'bf_protection_';
const IP_STORAGE_KEY_PREFIX = 'ip_protection_';

export const BRUTE_FORCE_CONFIG = {
  maxAttempts: 5, // Max failed attempts before lockout
  lockoutDuration: 15 * 60 * 1000, // 15 minutes in milliseconds
  ipMaxAttempts: 10, // Max attempts per IP
  ipLockoutDuration: 30 * 60 * 1000, // 30 minutes for IP lockout
  progressiveDelayBase: 1000, // Base delay in milliseconds
  maxProgressiveDelay: 30000, // Maximum progressive delay (30 seconds)
  resetWindow: 60 * 60 * 1000, // Reset counter after 1 hour of no attempts
};

// Store failed attempts in localStorage (client-side tracking)
export const recordFailedAttempt = (identifier: string, isIpBased = false) => {
  const storageKey = (isIpBased ? IP_STORAGE_KEY_PREFIX : STORAGE_KEY_PREFIX) + identifier;
  const now = Date.now();

  // Get existing attempts
  const attempts = getStoredAttempts(identifier, isIpBased);

  // Add new attempt
  attempts.push(now);

  // Store updated attempts
  localStorage.setItem(storageKey, JSON.stringify(attempts));

  return attempts.length;
};

export const getStoredAttempts = (identifier: string, isIpBased = false) => {
  const storageKey = (isIpBased ? IP_STORAGE_KEY_PREFIX : STORAGE_KEY_PREFIX) + identifier;
  const stored = localStorage.getItem(storageKey);
  const now = Date.now();

  if (!stored) {
    return [];
  }

  try {
    const attempts = JSON.parse(stored);
    // Filter out attempts outside the reset window
    return attempts.filter((timestamp: number) => now - timestamp < BRUTE_FORCE_CONFIG.resetWindow);
  } catch (_error) {
    // Error parsing stored attempts - return empty array as fallback
    return [];
  }
};

export const clearStoredAttempts = (identifier: string, isIpBased = false) => {
  const storageKey = (isIpBased ? IP_STORAGE_KEY_PREFIX : STORAGE_KEY_PREFIX) + identifier;
  localStorage.removeItem(storageKey);
};

export const checkBruteForceProtection = (identifier: string, userIP: string | null = null) => {
  const now = Date.now();

  // Check account-specific attempts
  const accountAttempts = getStoredAttempts(identifier, false);
  const recentAccountAttempts = accountAttempts.filter(
    (timestamp: number) => now - timestamp < BRUTE_FORCE_CONFIG.lockoutDuration
  );

  // Check IP-based attempts if IP is provided
  let recentIpAttempts = [];
  if (userIP) {
    const ipAttempts = getStoredAttempts(userIP, true);
    recentIpAttempts = ipAttempts.filter(
      (timestamp: number) => now - timestamp < BRUTE_FORCE_CONFIG.ipLockoutDuration
    );
  }

  // Determine if account is locked
  const isAccountLocked = recentAccountAttempts.length >= BRUTE_FORCE_CONFIG.maxAttempts;
  const isIpLocked = !!(userIP && recentIpAttempts.length >= BRUTE_FORCE_CONFIG.ipMaxAttempts);
  // Calculate time remaining for lockouts
  let accountLockoutTimeRemaining = 0;
  let ipLockoutTimeRemaining = 0;

  if (isAccountLocked && recentAccountAttempts.length > 0) {
    const oldestRecentAttempt = Math.min(...recentAccountAttempts);
    accountLockoutTimeRemaining = Math.max(
      0,
      BRUTE_FORCE_CONFIG.lockoutDuration - (now - oldestRecentAttempt)
    );
  }

  if (isIpLocked && recentIpAttempts.length > 0) {
    const oldestRecentIpAttempt = Math.min(...recentIpAttempts);
    ipLockoutTimeRemaining = Math.max(
      0,
      BRUTE_FORCE_CONFIG.ipLockoutDuration - (now - oldestRecentIpAttempt)
    );
  }

  // Calculate progressive delay for next attempt
  const totalAttempts = accountAttempts.length;
  const progressiveDelay = Math.min(
    BRUTE_FORCE_CONFIG.progressiveDelayBase * 2 ** Math.max(0, totalAttempts - 2),
    BRUTE_FORCE_CONFIG.maxProgressiveDelay
  );

  return {
    isBlocked: isAccountLocked || isIpLocked,
    isAccountLocked,
    isIpLocked,
    accountAttempts: recentAccountAttempts.length,
    ipAttempts: recentIpAttempts.length,
    maxAccountAttempts: BRUTE_FORCE_CONFIG.maxAttempts,
    maxIpAttempts: BRUTE_FORCE_CONFIG.ipMaxAttempts,
    accountLockoutTimeRemaining,
    ipLockoutTimeRemaining,
    progressiveDelay,
    attemptsRemaining: Math.max(0, BRUTE_FORCE_CONFIG.maxAttempts - recentAccountAttempts.length),
  };
};

export type BruteForceProtectionStatus = ReturnType<typeof checkBruteForceProtection>;

export const handleSuccessfulLogin = (identifier: string, userIP: string | null = null) => {
  // Clear stored attempts on successful login
  clearStoredAttempts(identifier, false);
  if (userIP) {
    clearStoredAttempts(userIP, true);
  }
};

export const formatTimeRemaining = (milliseconds: number) => {
  if (milliseconds <= 0) {
    return '0 seconds';
  }

  const minutes = Math.floor(milliseconds / (60 * 1000));
  const seconds = Math.floor((milliseconds % (60 * 1000)) / 1000);

  if (minutes > 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ${seconds} second${seconds !== 1 ? 's' : ''}`;
  } else {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  }
};

// Implement progressive delay
export const applyProgressiveDelay = async (delayMs: number) => {
  if (delayMs <= 0) {
    return;
  }

  return new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
};

// Get user's IP address (client-side approximation)
export const getUserIP = async () => {
  try {
    // In a real production environment, you'd want to get this from the server
    // For now, we'll use a simple approximation
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (_error) {
    // Error getting IP address - fallback to session-based identifier
    let sessionId = localStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = `session_${Math.random().toString(36).substr(2, 9)}${Date.now()}`;
      localStorage.setItem('session_id', sessionId);
    }
    return sessionId;
  }
};

// Security monitoring - track suspicious patterns
export const detectSuspiciousActivity = (identifier: string, userIP: string | null = null) => {
  const accountAttempts = getStoredAttempts(identifier, false);
  const ipAttempts = userIP ? getStoredAttempts(userIP, true) : [];

  const suspiciousPatterns = [];

  // Rapid successive attempts (more than 3 in 1 minute)
  const oneMinuteAgo = Date.now() - 60000;
  const recentAttempts = accountAttempts.filter((timestamp: number) => timestamp > oneMinuteAgo);
  if (recentAttempts.length >= 3) {
    suspiciousPatterns.push('rapid_attempts');
  }

  // High volume from IP
  if (ipAttempts.length >= 20) {
    suspiciousPatterns.push('high_volume_ip');
  }

  // Distributed attack pattern (same IP trying multiple accounts)
  if (userIP && ipAttempts.length >= 5) {
    suspiciousPatterns.push('distributed_attack');
  }

  return {
    isSuspicious: suspiciousPatterns.length > 0,
    patterns: suspiciousPatterns,
    riskLevel:
      suspiciousPatterns.length >= 2 ? 'high' : suspiciousPatterns.length === 1 ? 'medium' : 'low',
  };
};
